/**
 * Predictive Search — fullscreen modal: empty state, suggestions + product grid,
 * fallback collection, keyboard navigation, Cmd+K shortcut.
 */
import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton', 'emptyState'];

  #controller = new AbortController();
  #currentIndex = -1;

  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    document.addEventListener('keydown', this.#handleShortcut, { signal });

    if (this.dialog) {
      this.dialog.addEventListener(DialogCloseEvent.eventName, this.#onClose, { signal });
      this.dialog.addEventListener(DialogOpenEvent.eventName, this.#onOpen, { signal });
    }

    // Fallback: bind input event directly in case delegation doesn't fire
    requestAnimationFrame(() => {
      if (this.refs.searchInput) {
        this.refs.searchInput.addEventListener('input', () => this.search(), { signal });
      }
    });

    // Close dialog when clicking any link inside results
    this.addEventListener('click', (e) => {
      const link = e.target.closest('a[href]');
      if (link && link.getAttribute('href') !== '#') {
        this.dialog?.closeDialog();
      }
    }, { signal });

    // Collapse search bar margin on scroll for more space
    if (this.refs.scrollArea && this.refs.searchBarWrap) {
      this.refs.scrollArea.addEventListener('scroll', () => {
        const scrolled = this.refs.scrollArea.scrollTop > 10;
        this.refs.searchBarWrap.classList.toggle('tw:mt-0', scrolled);
        this.refs.searchBarWrap.classList.toggle('tw:mt-15', !scrolled);
      }, { signal, passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleShortcut = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.dialog?.toggleDialog();
    }
  };

  #onOpen = () => {
    this.#showEmptyState();
    // Reset search bar margin and scroll position
    if (this.refs.searchBarWrap) {
      this.refs.searchBarWrap.classList.add('tw:mt-15');
      this.refs.searchBarWrap.classList.remove('tw:mt-0');
    }
    if (this.refs.scrollArea) this.refs.scrollArea.scrollTop = 0;
    requestAnimationFrame(() => this.refs.searchInput?.focus());
  };

  #onClose = () => {
    this.#reset();
  };

  /** Debounced search on input (typing mode) */
  search = debounce(() => {
    this.#doSearch(false);
  }, 200);

  /** Immediate confirmed search (Enter / loupe click) */
  #confirmedSearch() {
    this.#doSearch(true);
  }

  #doSearch(confirmed) {
    const term = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!term.length) {
      this.#showEmptyState();
      this.#hideResetButton();
      return;
    }

    this.#showResetButton();
    this.#hideEmptyState();
    this.#showResults();
    this.#showLoading();
    this.#getSearchResults(term, confirmed);
  }

  /** Keyboard navigation */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#reset();
      this.dialog?.closeDialog();
      return;
    }

    const items = this.#getAllItems();
    if (!items.length) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#currentIndex = this.#currentIndex < items.length - 1 ? this.#currentIndex + 1 : 0;
        this.#highlightItem(items);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#currentIndex = this.#currentIndex > 0 ? this.#currentIndex - 1 : items.length - 1;
        this.#highlightItem(items);
        break;
      case 'Enter':
        if (this.#currentIndex >= 0 && items[this.#currentIndex]) {
          event.preventDefault();
          items[this.#currentIndex].click();
        }
        break;
    }
  };

  onSubmit(event) {
    event.preventDefault();
    this.#confirmedSearch();
  }

  resetSearch = debounce(() => {
    this.refs.searchInput.focus();
    this.#reset();
    this.#showEmptyState();
  }, 100);

  // ─── State management ──────────────────────────────────────────────────────

  #showEmptyState() {
    if (this.refs.emptyState) this.refs.emptyState.hidden = false;
    this.#hideResults();
  }

  #hideEmptyState() {
    if (this.refs.emptyState) this.refs.emptyState.hidden = true;
  }

  #showResults() {
    if (this.refs.predictiveSearchResults) this.refs.predictiveSearchResults.hidden = false;
  }

  #showLoading() {
    if (!this.refs.predictiveSearchResults) return;
    this.refs.predictiveSearchResults.style.opacity = '0';
    this.refs.predictiveSearchResults.style.transition = 'none';
  }

  #hideResults() {
    if (this.refs.predictiveSearchResults) {
      this.refs.predictiveSearchResults.hidden = true;
      this.refs.predictiveSearchResults.innerHTML = '';
      this.refs.predictiveSearchResults.style.opacity = '';
      this.refs.predictiveSearchResults.style.transition = '';
    }
  }

  // ─── Fetch results ────────────────────────────────────────────────────────

  async #getSearchResults(term, confirmed = false) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', term);
    url.searchParams.set('resources[type]', 'product,collection,article');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      if (!html) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newResults = doc.getElementById('predictive-search-results');

      if (newResults && this.refs.predictiveSearchResults) {
        // Build suggestions from product titles (typing mode only)
        const suggestionsHTML = confirmed ? '' : this.#buildSuggestionsFromResults(newResults, term);

        this.refs.predictiveSearchResults.innerHTML = suggestionsHTML + newResults.innerHTML;

        // Fade in results
        requestAnimationFrame(() => {
          this.refs.predictiveSearchResults.style.transition = 'opacity .35s ease-out';
          this.refs.predictiveSearchResults.style.opacity = '1';
        });

        // Toggle typing vs confirmed elements
        const container = this.refs.predictiveSearchResults;
        const resultCount = container.querySelector('[data-result-count]');
        const titleTyping = container.querySelector('[data-title-typing]');
        const titleConfirmed = container.querySelector('[data-title-confirmed]');

        if (resultCount) resultCount.hidden = !confirmed;
        if (titleTyping) titleTyping.hidden = confirmed;
        if (titleConfirmed) titleConfirmed.hidden = !confirmed;
      }
    } catch (err) {
      console.error('Predictive search error:', err);
    }
  }

  /** Generate keyword suggestions from product titles */
  #buildSuggestionsFromResults(resultsEl, term) {
    const titles = [...resultsEl.querySelectorAll('.product-card .tw\\:font-heading')]
      .map(el => el.textContent?.trim())
      .filter(Boolean);

    if (!titles.length) return '';

    const termLower = term.toLowerCase();
    const seen = new Set();
    const suggestions = [];

    for (const title of titles) {
      const titleLower = title.toLowerCase();
      if (titleLower.includes(termLower) && !seen.has(titleLower)) {
        seen.add(titleLower);
        const idx = titleLower.indexOf(termLower);
        const highlighted = this.#escapeHTML(title.substring(0, idx))
          + '<strong>' + this.#escapeHTML(title.substring(idx, idx + term.length)) + '</strong>'
          + this.#escapeHTML(title.substring(idx + term.length));
        suggestions.push({ text: title, highlighted });
      }
      if (suggestions.length >= 4) break;
    }

    if (!suggestions.length) return '';

    const suggestionsTitle = Theme.translations?.suggestions || 'Suggestions';
    return `
      <div class="tw:flex tw:flex-wrap tw:gap-x-8 tw:md:gap-x-12 tw:gap-y-4 tw:md:gap-y-6 tw:mb-6 tw:md:mb-8 tw:pb-6 tw:md:pb-8 tw:max-w-[700px] tw:mx-auto">
        <div>
          <p class="tw:text-4xl tw:font-heading tw:mb-6">${suggestionsTitle}</p>
          <ul class="tw:list-none tw:p-0 tw:m-0 tw:flex tw:flex-col tw:gap-0.5">
            ${suggestions.map(s => `
              <li>
                <a href="/search?q=${encodeURIComponent(s.text)}" class="tw:flex tw:items-center tw:gap-2.5 tw:py-1.5 tw:text-sm tw:text-muted tw:no-underline tw:hover:text-primary tw:transition-colors" ref="resultsItems[]">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="#C69B4A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M20.9999 21L16.6499 16.65" stroke="#C69B4A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>${s.highlighted}</span>
                </a>
              </li>
            `).join('')}
          </ul>
        </div>
      </div>`;
  }

  #escapeHTML(str) {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  #reset() {
    this.refs.searchInput.value = '';
    this.#currentIndex = -1;
    this.#hideResetButton();
    this.#hideResults();
  }

  #showResetButton() {
    if (this.refs.resetButton) this.refs.resetButton.hidden = false;
  }

  #hideResetButton() {
    if (this.refs.resetButton) this.refs.resetButton.hidden = true;
  }

  #getAllItems() {
    if (this.refs.predictiveSearchResults?.hidden) return [];
    return [...this.refs.predictiveSearchResults.querySelectorAll('[ref="resultsItems[]"]')];
  }

  #highlightItem(items) {
    items.forEach((item, i) => {
      if (i === this.#currentIndex) {
        item.setAttribute('aria-selected', 'true');
        item.style.background = 'var(--color-surface)';
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } else {
        item.removeAttribute('aria-selected');
        item.style.background = '';
      }
    });
    this.refs.searchInput.focus();
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
